import React, { useState, useEffect } from "react";
import axios from "axios";
import { shallow } from "zustand/shallow";
import { BrowserRouter, Route, Routes, Link } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Button, Card, useMantineTheme } from "@mantine/core";
import styled from "styled-components";

import { SuspenseWrapper } from "@components/shared";

import { AuthLayout, ForgotPassword, LoginForm, PasskeyEnrollPrompt } from "@components/Auth";

import { UserLogin } from "@components/Auth";

import { AdminContainer, UserContainer, CodeContainer } from "./containers";

import { Code } from "@components/Auth";
import { TicketEntryForm } from "@components/Ticket";
import appStore from "@duck/app-store";
import { setSentryUserFromStore } from "@util/sentry";

export default function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { admin, user, setAdmin, setUser, setPermissions } = appStore(
    (state) => ({
      admin: state.admin,
      user: state.user,
      setAdmin: state.setAdmin,
      setUser: state.setUser,
      setPermissions: state.setPermissions,
    }),
    shallow
  );

  const printReport =
    window.location.pathname.indexOf("/report-clearscreen/") === 0 &&
    Boolean(new URLSearchParams(window.location.search).get("print_ticket"));

  useEffect(() => {
    if (printReport) {
      setLoading(false);
      return;
    }
    fetchData();
  }, []);

  function attemptRedirect(res) {
    const entryPoint = localStorage.getItem("clearscreen-entry-point");

    if (JSON.stringify(res) === "{}" || JSON.stringify(res) === "null") {
      if (
        window.location.pathname.indexOf("code") === -1 &&
        window.location.pathname.indexOf("submit-trouble-ticket") === -1
      ) {
        if (entryPoint) {
          if (
            window.location.pathname.indexOf("admin") < 0 &&
            window.location.pathname.indexOf("user") < 0 &&
            window.location.pathname.indexOf("user-login") < 0
          ) {
            return (window.location = entryPoint);
          }
        }
      }
    }
  }

  function fetchData() {
    axios
      .get(`/status/`)
      .then(({ data }) => {
        const res = data.response[0];
        if (
          data.response &&
          data.response.length &&
          data.response[0] !== null &&
          data.response[0].server_session_token
        ) {
          axios.interceptors.request.use(
            (request) => {
              request.headers["server-session-token"] =
                data.response[0].server_session_token;

              return request;
            },
            (error) => {
              return Promise.reject(error);
            }
          );
        }
        attemptRedirect(data.response[0]);
        if (res === null) {
          return setLoading(false);
        }
        if (res.admin_id) {
          setPermissions(res.permissions || []);
          setLoading(false);
          setAdmin(res.current_user_hash);
          setSentryUserFromStore();
          return;
        }
        if (res.user_id) {
          setPermissions(res.permissions || []);
          setLoading(false);
          setUser(res.current_user_hash);
          setSentryUserFromStore();
          return;
        }
        setPermissions([]);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }

  if (printReport) {
    return (
      <SuspenseWrapper>
        <Auth>
          <AdminContainer />
        </Auth>
      </SuspenseWrapper>
    );
  }

  if (loading) return <></>;
  // if (error) return <AuthLayout title={error} />;

  if ((user || admin) && window.location.href.indexOf("code") > 0) {
    return (
      <>
        <SuspenseWrapper>
          <CodeContainer />
        </SuspenseWrapper>
      </>
    );
  }

  if (window.location.href.indexOf("submit-trouble-ticket") > 0) {
    return (
      <TicketHolder>
        <Toaster />
        <TicketEntryForm />
      </TicketHolder>
    );
  }

  if (admin)
    return (
      <SuspenseWrapper>
        <Auth>
          <PasskeyEnrollPrompt />
          <AdminContainer />
        </Auth>
      </SuspenseWrapper>
    );
  if (user) {
    return (
      <SuspenseWrapper>
        <Auth>
          <PasskeyEnrollPrompt />
          <UserContainer />
        </Auth>
      </SuspenseWrapper>
    );
  }

  return (
    <SuspenseWrapper>
      <React.Fragment>
        <Toaster />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<NonAuth />} />
            <Route path="admin" element={<LoginView isAdmin />} />
            <Route
              path="/admin-forgot-password"
              element={<ForgotView isAdmin />}
            />
            <Route path="user" element={<LoginView isUser />} />
            <Route
              path="/user-forgot-password"
              element={<ForgotView isUser />}
            />
            <Route path="user-login/:id/*" element={<UserLogin />} />
            <Route path="code/:id" element={<Code />} />
            <Route path="*" element={<NonAuth />} />
          </Routes>
        </BrowserRouter>
      </React.Fragment>
    </SuspenseWrapper>
  );
}

const NonAuth = () => (
  <AuthLayout title="Not Logged In!">
    {/* <p>You're not logged in.</p> */}
    <Button component={Link} to={`/user`} variant="light">
      user Login
    </Button>
  </AuthLayout>
);

const ForgotView = ({ isAdmin, isUser }) => {
  return (
    <AuthLayout>
      <ForgotPassword isUser={isUser} isAdmin={isAdmin} />
      <Button
        size="xs"
        component={Link}
        to={isAdmin ? `/admin` : isUser ? `/user` : "/user"}
        variant="light"
        mt="lg"
      >
        Remember password?
      </Button>
    </AuthLayout>
  );
};

const LoginView = ({ isAdmin, isUser }) => {
  return (
    <AuthLayout>
      <LoginForm isAdmin={isAdmin} isUser={isUser} />
      <Button
        component={Link}
        to={
          isAdmin
            ? `/admin-forgot-password`
            : isUser
            ? `/user-forgot-password`
            : `/user-forgot-password`
        }
        variant="light"
        size="xs"
        mt="lg"
      >
        Forgot password?
      </Button>
    </AuthLayout>
  );
};

const TicketHolder = ({ children }) => {
  const theme = useMantineTheme();
  return (
    <Wrapper backgroundColor={theme.colors.dark[7]}>
      <div className="wrapper">
        <Card>{children}</Card>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(props) => props.backgroundColor};

  .wrapper {
    width: 100%;
    width: 375px;
    background: #fff;
  }
`;

const Auth = ({ children }) => (
  <React.Fragment>
    <Toaster />
    <BrowserRouter>{children}</BrowserRouter>
  </React.Fragment>
);
